import type * as api from '@gitbook/api-client';
import { assertNever } from '@gitbook/util-ts';

export function flattenGroupedSections(
    structure: (api.SiteSection | api.SiteSectionGroup)[]
): api.SiteSection[] {
    return structure.flatMap((item) =>
        item.object === 'site-section' ? item : getSiteSectionsFromSiteSectionGroup(item)
    );
}

export function flattenGroupedSectionGroups(
    structure: (api.SiteSection | api.SiteSectionGroup)[]
): api.SiteSectionGroup[] {
    return structure.flatMap((item) =>
        item.object === 'site-section-group'
            ? [item, ...flattenGroupedSectionGroups(item.children)]
            : []
    );
}

/**
 * Get the site spaces from the site structure.
 */
export function getSiteSpacesFromStructure(siteStructure: api.SiteStructure): api.SiteSpace[] {
    switch (siteStructure.type) {
        case 'siteSpaces': {
            return siteStructure.structure;
        }
        case 'sections': {
            return siteStructure.structure.flatMap((sectionOrGroup) => {
                switch (sectionOrGroup.object) {
                    case 'site-section':
                        return sectionOrGroup.siteSpaces;
                    case 'site-section-group':
                        return getSiteSpacesFromSiteSectionGroup(sectionOrGroup);
                    default:
                        assertNever(sectionOrGroup);
                }
            });
        }
        default:
            assertNever(siteStructure);
    }
}

/**
 * Check if the site structure is empty.
 */
export function checkIsSiteStructureEmpty(siteStructure: api.SiteStructure): boolean {
    if (siteStructure.type === 'siteSpaces') {
        return siteStructure.structure.length === 0;
    }
    return false;
}

export function flattenSiteStructure(
    siteStructure: api.SiteStructure
): (api.SiteSectionGroup | api.SiteSection | api.SiteSpace)[] {
    const siteSpaces = getSiteSpacesFromStructure(siteStructure);
    if (siteStructure.type === 'sections') {
        return [
            ...flattenGroupedSections(siteStructure.structure),
            ...flattenGroupedSectionGroups(siteStructure.structure),
            ...siteSpaces,
        ];
    }
    return siteSpaces;
}

function getIndexInSiteItems(
    items: (api.SiteSection | api.SiteSectionGroup | api.SiteSpace)[],
    itemId: string
): number {
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.id === itemId) {
            return i;
        }
        const childIndex = (() => {
            switch (item.object) {
                case 'site-section-group':
                    return getIndexInSiteItems(item.children, itemId);
                case 'site-section':
                    return getIndexInSiteItems(item.siteSpaces, itemId);
                case 'site-space':
                    return -1;
                default:
                    assertNever(item);
            }
        })();
        if (childIndex !== -1) {
            return childIndex;
        }
    }
    return -1;
}

export function getIndexInSiteStructure(siteStructure: api.SiteStructure, itemId: string): number {
    return getIndexInSiteItems(siteStructure.structure, itemId);
}

export function getSiblingsInSiteItems(
    items: (api.SiteSection | api.SiteSectionGroup | api.SiteSpace)[],
    itemId: string
) {
    if (items.find((item) => item.id === itemId)) {
        return items;
    }
    for (const item of items) {
        const childItems = (() => {
            switch (item.object) {
                case 'site-section-group':
                    return getSiblingsInSiteItems(item.children, itemId);
                case 'site-section':
                    return getSiblingsInSiteItems(item.siteSpaces, itemId);
                case 'site-space':
                    return null;
                default:
                    assertNever(item);
            }
        })();
        if (childItems) {
            return childItems;
        }
    }
    return null;
}

export function getSiblingsInSiteStructure(siteStructure: api.SiteStructure, itemId: string) {
    return getSiblingsInSiteItems(siteStructure.structure, itemId);
}

/**
 * Recursively get all the sections starting from a section group.
 */
export function getSiteSectionsFromSiteSectionGroup(
    sectionGroup: api.SiteSectionGroup
): api.SiteSection[] {
    return sectionGroup.children.flatMap((child) =>
        child.object === 'site-section' ? [child] : getSiteSectionsFromSiteSectionGroup(child)
    );
}

/**
 * Recursively get all the site spaces starting from a section group.
 */
export function getSiteSpacesFromSiteSectionGroup(
    sectionGroup: api.SiteSectionGroup
): api.SiteSpace[] {
    return sectionGroup.children.flatMap((child) =>
        child.object === 'site-section'
            ? child.siteSpaces
            : getSiteSpacesFromSiteSectionGroup(child)
    );
}
